import { useEffect, useState } from 'react'
import { Field, Row, Text, TimeZoneSelect } from '../components/Form'
import { useCollection } from '../lib/firestore'
import { SHOW_TIMINGS } from '../lib/itineraryTypes'
import { browserTimeZone, toDateInputValue, toTimeInputValue } from '../lib/zonedTime'
import { locate } from '../lib/geocode'
import type { ShowDraft } from '../lib/mutations'
import type { Show, Venue, WithId } from '../types/models'

/* ============================================================================
   The show form, once.

   Used by `ShowDialog` (edit, in a modal) and by the New Tour flow (create, in
   the page). Extracted rather than copied — two copies of a form drift, and
   the running-order block in particular has to stay in step with the batch in
   `createShow` that writes each timing as an itinerary item.
   ============================================================================ */

/** Sensible opening times, so a new show isn't six empty fields. */
const DEFAULT_TIMINGS: Record<string, string> = {
  venueAccess: '12:00',
  loadIn: '15:00',
  soundcheck: '17:00',
  doors: '19:00',
  headline: '20:00',
  packOut: '23:00',
}

export function ShowFields({
  draft,
  patch,
  autoFocus = true,
}: {
  draft: ShowDraft
  patch: (next: Partial<ShowDraft>) => void
  /**
   * Off for the in-page flow. Focusing the first field of a step that sits
   * below the fold scrolls the browser straight past the step's own heading,
   * so the reader lands mid-form with no idea which step they're on. In a
   * modal there is nothing to scroll past, so it stays on by default.
   */
  autoFocus?: boolean
}) {
  const setTiming = (id: string, value: string | null) =>
    patch({ timings: { ...draft.timings, [id]: value } })

  return (
    <div className="stack" style={{ gap: 16 }}>
      <section className="stack">
        <h3 className="t-eyebrow">Date</h3>
        <Field label="Show date">
          <Text type="date" value={draft.date} onChange={(value) => patch({ date: value })} />
        </Field>
      </section>

      <section className="stack">
        <h3 className="t-eyebrow">Venue</h3>
        <VenueLookup draft={draft} patch={patch} autoFocus={autoFocus} />
      </section>

      <section className="stack">
        <h3 className="t-eyebrow">Running order</h3>
        <p className="t-caption">
          Each of these becomes an itinerary item on the day sheet. Clear a time to drop it.
        </p>
        <div className="panel rows">
          {SHOW_TIMINGS.map((timing) => {
            const value = draft.timings[timing.id] ?? ''
            return (
              <div key={timing.id} className="spread" style={{ padding: '9px 14px' }}>
                <span className="t-body">{timing.label}</span>
                <div className="row" style={{ gap: 8 }}>
                  <input
                    className="field"
                    type="time"
                    value={value}
                    style={{ width: 130 }}
                    onChange={(event) => setTiming(timing.id, event.target.value || null)}
                  />
                  <button
                    type="button"
                    className="btn btn-quiet"
                    aria-label={`Clear ${timing.label}`}
                    disabled={!value}
                    onClick={() => setTiming(timing.id, null)}
                  >
                    ✕
                  </button>
                </div>
              </div>
            )
          })}
        </div>

        <Field label="Set length (minutes)">
          <Text
            type="number"
            value={String(draft.setDurationMinutes)}
            onChange={(value) => patch({ setDurationMinutes: Number(value) || 0 })}
          />
        </Field>
      </section>

      <section className="stack">
        <h3 className="t-eyebrow">Venue contact</h3>
        <p className="t-caption">
          Saved to your contacts and attached to this venue, so it's there next time you play here.
        </p>
        <Field label="Name">
          <Text value={draft.contactName} onChange={(value) => patch({ contactName: value })} />
        </Field>
        <Row>
          <Field label="Email">
            <Text
              type="email"
              value={draft.contactEmail}
              onChange={(value) => patch({ contactEmail: value })}
            />
          </Field>
          <Field label="Phone">
            <Text value={draft.contactPhone} onChange={(value) => patch({ contactPhone: value })} />
          </Field>
        </Row>
      </section>
    </div>
  )
}

/* ---------------------------------------------------------------- lookup */

/**
 * Venue search, mirroring `AddShowView.swift`.
 *
 * Type a venue, pick it from the list, and the address fields fill themselves.
 * The address block stays hidden until either a venue is chosen or "enter
 * address manually" is clicked — six empty address fields as the opening state
 * makes a two-second task look like a form.
 *
 * The Mac searches MapKit, which also hands back a timezone. There is no
 * keyless web equivalent, so this searches the venues already saved to the
 * account first (instant, and the right answer for anywhere you have played
 * before) and falls back to OpenStreetMap.
 *
 * Picking a venue also resolves its TIMEZONE from its coordinates. It used to
 * be left on whatever `browserTimeZone()` returned, which meant a tour manager
 * in Auckland adding a Berkeley date wrote `Pacific/Auckland` onto the show and
 * every time on it after that was twenty-one hours out. A venue's timezone
 * belongs to the venue, never to whoever typed it in.
 */
function VenueLookup({
  draft,
  patch,
  autoFocus,
}: {
  draft: ShowDraft
  patch: (next: Partial<ShowDraft>) => void
  autoFocus: boolean
}) {
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<VenueSuggestion[]>([])
  const [searching, setSearching] = useState(false)
  const [manual, setManual] = useState(false)
  const [chosen, setChosen] = useState(false)
  const [zoning, setZoning] = useState(false)

  // Saved venues are read once and filtered in memory. The list is small, and
  // a query-per-keystroke against Firestore would cost reads for no benefit.
  const saved = useCollection<Venue>('venues', [])

  useEffect(() => {
    const term = query.trim()
    if (term.length < 3 || chosen) {
      setResults([])
      return
    }

    // Debounced: OpenStreetMap asks for low volume, and a request per
    // keystroke is both rude and slower than waiting for someone to stop
    // typing.
    const timer = setTimeout(async () => {
      setSearching(true)
      const local = matchSaved(saved.data, term)
      setResults(local)
      try {
        const remote = await searchPlaces(term)
        // Saved venues stay on top — a place you have played is a better
        // answer than a map result with the same name.
        setResults([...local, ...remote.filter((row) => !local.some((own) => own.name === row.name))])
      } catch {
        /* offline, or the search is down — the saved matches still stand */
      } finally {
        setSearching(false)
      }
    }, 350)

    return () => clearTimeout(timer)
  }, [query, chosen, saved.data])

  function choose(result: VenueSuggestion) {
    patch({
      venueName: result.name,
      address: result.address,
      city: result.city,
      country: result.country,
    })
    setQuery(result.name)
    setResults([])
    setChosen(true)

    // The timezone follows the place. Resolved in the background so picking a
    // venue stays instant; if the lookup fails the field simply keeps whatever
    // it had, and it is editable either way.
    setZoning(true)
    const where = [result.name, result.address, result.city, result.country]
      .filter(Boolean)
      .join(', ')
    void locate(where)
      .then((found) => {
        if (found.timezone) patch({ timezone: found.timezone })
      })
      .finally(() => setZoning(false))
  }

  const expanded = manual || chosen || draft.venueName.length > 0

  return (
    <div className="panel panel-pad stack" style={{ gap: 12 }}>
      <Field label="Search for a venue">
        <Text
          value={query}
          onChange={(value) => {
            setQuery(value)
            // Editing the search after picking something means they are
            // looking again, so the suggestions come back.
            if (chosen) setChosen(false)
          }}
          autoFocus={autoFocus}
        />
      </Field>

      {results.length > 0 && (
        <div className="panel rows" style={{ background: 'var(--panel-raised)' }}>
          {results.slice(0, 6).map((result) => (
            <button
              key={`${result.name}-${result.address}`}
              type="button"
              className="stack"
              style={{
                gap: 2,
                width: '100%',
                textAlign: 'left',
                padding: '10px 12px',
                cursor: 'pointer',
                background: 'transparent',
                border: 'none',
              }}
              onClick={() => choose(result)}
            >
              <span className="row" style={{ gap: 7 }}>
                <span style={{ fontWeight: 600 }}>{result.name}</span>
                {result.isSaved && <span className="chip">Saved</span>}
              </span>
              <span className="t-spec-caption faint truncate">{result.address}</span>
            </button>
          ))}
        </div>
      )}

      {searching && results.length === 0 && <span className="t-caption">Searching…</span>}

      {!expanded && (
        <button
          type="button"
          className="btn btn-quiet"
          style={{ alignSelf: 'flex-start', paddingLeft: 0, color: 'var(--accent-text)' }}
          onClick={() => setManual(true)}
        >
          Can't find it? Enter address manually
        </button>
      )}

      {expanded && (
        <div className="stack" style={{ gap: 12 }}>
          {/* No example values in these. A greyed-out address reads as filled
              in at a glance, which is the one mistake that matters on a field
              a promoter is going to be sent. */}
          <Field label="Venue name">
            <Text value={draft.venueName} onChange={(value) => patch({ venueName: value })} />
          </Field>
          <Field label="Address">
            <Text value={draft.address} onChange={(value) => patch({ address: value })} />
          </Field>
          <Row>
            <Field label="City">
              <Text value={draft.city} onChange={(value) => patch({ city: value })} />
            </Field>
            <Field label="Country">
              <Text value={draft.country} onChange={(value) => patch({ country: value })} />
            </Field>
          </Row>
          <Field
            label="Timezone"
            hint="Every time below is read in this zone. Search can't tell us the zone — check it."
          >
            <TimeZoneSelect value={draft.timezone} onChange={(value) => patch({ timezone: value })} />
            {zoning && <span className="t-caption">Checking the venue&rsquo;s timezone…</span>}
          </Field>
        </div>
      )}
    </div>
  )
}

interface VenueSuggestion {
  name: string
  address: string
  city: string
  country: string
  isSaved?: boolean
}

function matchSaved(venues: Array<WithId<Venue>>, term: string): VenueSuggestion[] {
  const needle = term.toLowerCase()
  return venues
    .filter(
      (venue) =>
        venue.name.toLowerCase().includes(needle) || venue.city.toLowerCase().includes(needle),
    )
    .slice(0, 4)
    .map((venue) => ({
      name: venue.name,
      address: [venue.address, venue.city, venue.country].filter(Boolean).join(', '),
      city: venue.city,
      country: venue.country ?? '',
      isSaved: true,
    }))
}

/** OpenStreetMap, the same source `lib/geocode.ts` already uses for the map. */
async function searchPlaces(term: string): Promise<VenueSuggestion[]> {
  const url =
    'https://nominatim.openstreetmap.org/search' +
    `?format=jsonv2&addressdetails=1&limit=6&q=${encodeURIComponent(term)}`
  const response = await fetch(url, { headers: { Accept: 'application/json' } })
  if (!response.ok) throw new Error(String(response.status))

  const rows = (await response.json()) as Array<{
    name?: string
    display_name: string
    address?: Record<string, string>
  }>

  return rows.map((row) => {
    const parts = row.display_name.split(',').map((part) => part.trim())
    const address = row.address ?? {}
    const name = row.name || parts[0] || row.display_name

    // A street address, built from the structured fields rather than the
    // whole display string. Nominatim's `display_name` repeats the venue
    // name, the city and the country back at you — dropping that straight
    // into the Address field puts "Auckland, Auckland, 1053, New Zealand"
    // on a document a promoter is going to read.
    const street = [
      [address.house_number, address.road].filter(Boolean).join(' '),
      address.suburb ?? address.neighbourhood,
    ]
      .filter(Boolean)
      .join(', ')

    return {
      name,
      // Falls back to the display string minus the leading venue name, which
      // is still better than repeating it.
      address: street || parts.filter((part) => part !== name).join(', '),
      city: address.city ?? address.town ?? address.village ?? address.suburb ?? parts[1] ?? '',
      country: address.country ?? parts[parts.length - 1] ?? '',
    }
  })
}

export function initialShowDraft(
  show?: WithId<Show> | null,
  defaultDate?: string,
): ShowDraft {
  if (!show) {
    return {
      venueName: '',
      address: '',
      city: '',
      country: '',
      timezone: browserTimeZone(),
      date: defaultDate ?? toDateInputValue(new Date()),
      timings: { ...DEFAULT_TIMINGS },
      setDurationMinutes: 60,
      contactName: '',
      contactEmail: '',
      contactPhone: '',
    }
  }

  const zone = show.timezone ?? browserTimeZone()
  const read = (value: Show[keyof Show]) =>
    value && typeof value === 'object' && 'toDate' in value
      ? toTimeInputValue((value as { toDate: () => Date }).toDate(), zone)
      : null

  return {
    venueName: show.venueName,
    address: show.venueAddress,
    city: show.city,
    country: show.country ?? '',
    timezone: zone,
    date: toDateInputValue(show.date.toDate(), zone),
    timings: {
      venueAccess: read(show.venueAccess),
      loadIn: read(show.loadIn),
      soundcheck: read(show.soundCheck),
      doors: read(show.doorsOpen),
      headline: read(show.headlinerSetTime),
      packOut: read(show.packOut),
    },
    setDurationMinutes: show.headlinerSetDurationMinutes ?? 60,
    contactName: show.contactName ?? '',
    contactEmail: show.contactEmail ?? '',
    contactPhone: show.contactPhone ?? '',
  }
}

export function showIsValid(draft: ShowDraft): boolean {
  return draft.venueName.trim().length > 0 && draft.city.trim().length > 0 && draft.date.length > 0
}
